// Общие части экранов активностей: зоны пульса, сводка показателей, график пульса.

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Stat } from '@/components/ui'
import { formatDuration, formatNumber } from '@/lib/format'

import type { Activity } from './api'
import { formatDistance, hrPoints, zoneRows } from './logic'

const ZONE_COLORS = ['var(--muted)', 'var(--accent-soft)', 'var(--accent)', 'var(--warning)', 'var(--danger)']

export function ZoneBars({ zones }: { zones: number[] | null }) {
  const rows = zoneRows(zones)
  if (rows.length === 0) return <p className="text-sm text-muted">Зон пульса нет</p>
  return (
    <ul className="space-y-1.5" aria-label="Время в пульсовых зонах">
      {rows.map((z) => (
        <li key={z.zone} className="flex items-center gap-3 text-sm tabular-nums">
          <span className="w-14 text-muted">Зона {z.zone}</span>
          <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-2">
            <span
              className="block h-full rounded-full"
              style={{ width: `${z.percent}%`, background: ZONE_COLORS[z.zone - 1] }}
            />
          </span>
          <span className="w-24 text-right text-fg">{formatDuration(z.seconds)}</span>
          <span className="w-10 text-right text-muted">{z.percent}%</span>
        </li>
      ))}
    </ul>
  )
}

export function ActivityStats({ activity: a, compact = false }: { activity: Activity; compact?: boolean }) {
  const distance = formatDistance(a.distance_m)
  return (
    <div className={compact ? 'grid grid-cols-2 gap-3 sm:grid-cols-4' : 'grid grid-cols-2 gap-4 sm:grid-cols-4'}>
      <Stat label="Длительность" value={formatDuration(a.duration_s)} />
      <Stat
        label="Пульс"
        value={a.avg_hr ?? '—'}
        unit={a.avg_hr ? 'уд/мин' : undefined}
        hint={a.max_hr ? `макс. ${a.max_hr}` : undefined}
      />
      <Stat label="Калории" value={formatNumber(a.calories, { digits: 0 })} unit={a.calories ? 'ккал' : undefined} />
      <Stat
        label="Нагрузка"
        value={formatNumber(a.training_load, { digits: 0 })}
        hint={
          a.aerobic_te !== null || a.anaerobic_te !== null
            ? `TE ${formatNumber(a.aerobic_te)} / ${formatNumber(a.anaerobic_te)}`
            : undefined
        }
      />
      {!compact && distance ? <Stat label="Дистанция" value={distance} /> : null}
    </div>
  )
}

export function HrChart({ series }: { series: [number, number][] | null }) {
  const points = hrPoints(series)
  if (points.length === 0) return <p className="py-8 text-center text-sm text-muted">Детального пульса нет</p>
  return (
    <div className="h-64" role="img" aria-label="Пульс по времени">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="min"
            type="number"
            domain={['dataMin', 'dataMax']}
            tick={{ fill: 'var(--muted)', fontSize: 11 }}
            tickFormatter={(v: number) => `${Math.round(v)} мин`}
          />
          <YAxis domain={['dataMin - 5', 'dataMax + 5']} tick={{ fill: 'var(--muted)', fontSize: 11 }} />
          <Tooltip
            formatter={(value) => [`${String(value)} уд/мин`, 'Пульс']}
            labelFormatter={(label) => `${formatNumber(Number(label))} мин`}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
          />
          <Line type="monotone" dataKey="bpm" stroke="var(--danger)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
